import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import { Map, Marker, Polygon, Popup, TileLayer } from "react-leaflet";
import L from "leaflet";
import "components/MapLeft/leaflet.css";
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
import { getAssets } from "services/assetDataProvider";
import AssetBreadcrumb from "components/AssetBreadcrumb/AssetBreadcrumb.jsx";
import {
  getInlinePageChrome,
  createSelectableRowStyle
} from "theme/appShellTheme.js";

const { ui, shell, isDark } = getInlinePageChrome();

const styles = {
  pageShell: shell.pageShell,
  cardShell: shell.cardHover,
  cardHeader: shell.cardHeaderHover,
  cardBody: {
    padding: "18px 22px 24px"
  },
  title: {
    color: ui.text,
    margin: 0,
    fontWeight: 700,
    letterSpacing: "0.02em"
  },
  mapFrame: {
    padding: 14,
    borderRadius: 24,
    background: ui.surfaceRaised,
    boxShadow: ui.shadowInset,
    border: isDark ? `1px solid ${ui.border}` : undefined
  },
  drilldownItem: isSelected =>
    createSelectableRowStyle(ui, isDark, isSelected, { cursor: "pointer" })
};

const defaultIcon = L.icon({ iconUrl: icon, shadowUrl: iconShadow });
L.Marker.prototype.options.icon = defaultIcon;

function getAssetCenter(asset) {
  if (asset.location && asset.location.lat != null && asset.location.lng != null) {
    return [asset.location.lat, asset.location.lng];
  }

  if (
    asset.boundary_geojson &&
    asset.boundary_geojson.type === "Polygon" &&
    asset.boundary_geojson.coordinates &&
    asset.boundary_geojson.coordinates[0] &&
    asset.boundary_geojson.coordinates[0].length > 0
  ) {
    const coords = asset.boundary_geojson.coordinates[0];
    const sums = coords.reduce(
      (acc, point) => {
        acc.lng += point[0];
        acc.lat += point[1];
        return acc;
      },
      { lat: 0, lng: 0 }
    );
    return [sums.lat / coords.length, sums.lng / coords.length];
  }

  return null;
}

function polygonToLeafletPoints(geojson) {
  if (!geojson || geojson.type !== "Polygon" || !geojson.coordinates) {
    return null;
  }
  return geojson.coordinates[0].map(point => [point[1], point[0]]);
}

export default function AssetMap({ axios }) {
  const [assets, setAssets] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);

  useEffect(() => {
    getAssets(axios).then(data => {
      setAssets(data || []);
      if (data && data.length > 0) {
        setSelectedAsset(data[0]);
      }
    });
  }, [axios]);

  const mapCenter = useMemo(() => {
    if (selectedAsset) {
      const selectedCenter = getAssetCenter(selectedAsset);
      if (selectedCenter) {
        return selectedCenter;
      }
    }

    for (let i = 0; i < assets.length; i += 1) {
      const center = getAssetCenter(assets[i]);
      if (center) {
        return center;
      }
    }

    return [20, 0];
  }, [assets, selectedAsset]);

  const breadcrumbs = selectedAsset
    ? [{ id: selectedAsset.id, name: selectedAsset.name }]
    : [];

  return (
    <div style={styles.pageShell}>
      <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <AssetBreadcrumb chain={breadcrumbs} />
      </GridItem>
      <GridItem xs={12} sm={12} md={8}>
        <Card style={styles.cardShell}>
          <CardHeader color="transparent" style={styles.cardHeader}>
            <h4 style={styles.title}>Asset Map</h4>
          </CardHeader>
          <CardBody style={styles.cardBody}>
            <div style={styles.mapFrame}>
            <div style={{ height: 460, width: "100%" }}>
              <Map center={mapCenter} zoom={6} style={{ height: "100%", borderRadius: 20 }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                {assets.map(asset => {
                  const center = getAssetCenter(asset);
                  if (!center) {
                    return null;
                  }
                  const polygonPoints = polygonToLeafletPoints(asset.boundary_geojson);
                  return (
                    <React.Fragment key={asset.id}>
                      <Marker
                        position={center}
                        eventHandlers={{
                          click: () => setSelectedAsset(asset)
                        }}
                      >
                        <Popup>
                          <div>
                            <strong>{asset.name}</strong>
                            <div>
                              <Link to={`/assets/${asset.id}`}>Open details</Link>
                            </div>
                          </div>
                        </Popup>
                      </Marker>
                      {polygonPoints ? (
                        <Polygon
                          positions={polygonPoints}
                          color={selectedAsset && selectedAsset.id === asset.id ? "#2563eb" : "#16a34a"}
                        />
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </Map>
            </div>
            </div>
          </CardBody>
        </Card>
      </GridItem>
      <GridItem xs={12} sm={12} md={4}>
        <Card style={styles.cardShell}>
          <CardHeader color="transparent" style={styles.cardHeader}>
            <h4 style={styles.title}>Site Drill-Down</h4>
          </CardHeader>
          <CardBody style={styles.cardBody}>
            {assets.map(asset => (
              <div
                key={asset.id}
                style={styles.drilldownItem(selectedAsset && selectedAsset.id === asset.id)}
              >
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <strong style={{ color: "#122033" }}>{asset.name}</strong>
                  <button
                    type="button"
                    onClick={() => setSelectedAsset(asset)}
                    style={{ border: 0, background: "transparent", color: "#2563eb", cursor: "pointer" }}
                  >
                    Focus
                  </button>
                </div>
                <div>
                  <Link to={`/assets/${asset.id}`}>Details</Link>
                </div>
              </div>
            ))}
          </CardBody>
        </Card>
      </GridItem>
      </GridContainer>
    </div>
  );
}

AssetMap.propTypes = {
  axios: PropTypes.object.isRequired
};
